import React from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCheckInRoutes } from 'Hooks/useCheckInRoutes';
import { usePaymentCheckOut } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import paymentStore from 'Models/PaymentStore';
import If from 'Views/Components/If/If';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { Flex } from 'Views/Components/Flex/Flex';
import { PAYMENT_FORM_ID } from './usePayment';
import {
	cashPayment,
	eftposPayment,
	invoicePayment,
	payLater,
	createTerminalPayment,
	checkTerminalPayment,
	cancelTerminalPayment,
} from 'Services/Api/_HumanWritten/PaymentService';
import { onWizardSuccess } from 'Util/_HumanWritten/BookingWizard/BookingWizardUtils';
import alertToast from 'Util/ToastifyUtils';
import { terminalPaymentModal } from './TerminalPayment';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { PaymentUnavailable, useCardFieldsState } from './PaymentUnavailable';
import StripeCheckoutForm, { StripeCheckoutFormHandle } from './StripeCheckoutForm';
import SquareCheckoutForm, { SquareCheckoutFormHandle } from './SquareCheckoutForm';
import { KNOWN_NON_CARD_OPTIONS } from './PaymentTypeWrapper';
import { dispatchResetReservationEvent } from 'Events/HumanWritten/ResetReservationEvent';

/** About a minute of retries at two seconds apart, before a terminal poll is reported as failed. */
const MAX_TRANSIENT_POLL_FAILURES = 30;

/** No response at all, a rate limit, or a server/gateway error: none of them is an answer about the payment. */
function isTransientPollError(err: any): boolean {
	const status: number | undefined = err?.response?.status;
	return status === undefined || status === 429 || status >= 500;
}

/**
 * Provider-agnostic checkout orchestrator. Owns the form element, the submit handler, and
 * all non-card payment flows (cash, eftpos, invoice, pay-later, terminal). Card-based payment
 * collection (credit card and saved cards) is delegated to a provider-specific child form
 * via an imperative ref — Stripe today, Square in Phase B.
 *
 * Use sites (booking wizard, check-in, parking, gift voucher, anytime ticket, etc.) consume
 * this component unchanged via <c>PaymentTypeWrapper</c> → <c>PaymentProviderWrapper</c>.
 */
function CheckOutForm() {
	const intl = useIntl();
	const navigate = useNavigate();
	useCheckInRoutes();

	// Set when the card fields cannot be shown — the SDK is still loading, or it never arrived, or
	// the amount payable could not be worked out. Only the card slot below reacts to it; the form
	// element and every non-card method carry on regardless, which is the whole point of reporting
	// it here rather than by unmounting this component.
	const cardFieldsState = useCardFieldsState();

	const cardFormRef = React.useRef<StripeCheckoutFormHandle>(null);
	const squareFormRef = React.useRef<SquareCheckoutFormHandle>(null);

	const {
		handleError,
		setTabIsLoading,
		setMessage,
		setPaymentProcessing,
		setShowSurchargeModal,
		handleProviderNotLoaded,
		transactionId,
		userId,
		isAlteration,
		tripType,
		paymentMethod,
		includeSubmitButtons,
		inCheckIn,
		onConfirm,
		eventBooking,
		paymentProcessing,
		onCancelRedirectUrl,
		beforePayment,
		tabIsLoading,
		disableContinueButton,
		setClearTerminalPolling,
	} = usePaymentCheckOut();

	const provider = paymentStore.provider;
	const isSavedCard = !!paymentMethod && !KNOWN_NON_CARD_OPTIONS.includes(paymentMethod);
	const isCardPayment = paymentMethod === 'credit-card-option' || isSavedCard;

	const terminalPollingRef = React.useRef<{
		timeout: NodeJS.Timeout;
		terminal: string;
	} | null>(null);

	const clearTerminalPolling = React.useCallback(async () => {
		if (terminalPollingRef.current) {
			clearTimeout(terminalPollingRef.current.timeout);
			terminalPollingRef.current = null;
			await cancelTerminalPayment(transactionId);
		}
		setPaymentProcessing(false);
		setTabIsLoading(false);
	}, [transactionId]);

	// Stop an in-flight terminal poll and cancel its Square checkout, without touching the
	// loading flags (unlike clearTerminalPolling). Used before a manual cash/EFTPOS completion so
	// switching methods after the reader stalls doesn't leave the poll running (racing onSuccess)
	// or the reader live (charging the card afterwards). No-ops when no terminal payment is active.
	const stopTerminalPolling = React.useCallback(async () => {
		if (terminalPollingRef.current) {
			clearTimeout(terminalPollingRef.current.timeout);
			terminalPollingRef.current = null;
			try {
				await cancelTerminalPayment(transactionId);
			} catch {
				// The checkout may already be gone/cancelled; the server-side guard cancels too.
			}
		}
	}, [transactionId]);

	React.useEffect(() => {
		setClearTerminalPolling(clearTerminalPolling);
		return () => {
			setClearTerminalPolling(undefined);
		};
	}, [clearTerminalPolling, setClearTerminalPolling]);

	React.useEffect(() => {
		return () => {
			if (terminalPollingRef.current) {
				clearTimeout(terminalPollingRef.current.timeout);
			}
		};
	}, []);

	const onSuccess = (confirmAndCheckIn: boolean, transactionIds?: string[]) => {
		if (onConfirm) {
			onConfirm(confirmAndCheckIn ?? true);
		} else {
			onWizardSuccess(
				navigate,
				transactionId,
				isAlteration,
				tripType === 'return',
				eventBooking,
				userId,
				undefined,
				transactionIds,
			);
		}
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLInputElement>) => {
		e.preventDefault();

		// Wallet payment buttons (Apple Pay / Google Pay) live inside this <form>.
		// Clicking them can trigger form submission alongside the wallet's own onclick
		// handler. Bail out if a payment is already in progress to prevent the card
		// submit flow from interfering with the wallet flow.
		if (paymentProcessing) {
			return;
		}

		setMessage(null);
		setPaymentProcessing(true);
		setTabIsLoading(true);
		setShowSurchargeModal(false);

		let confirmAndCheckIn = true;

		// beforePayment is a generic pre-submit hook. Originally check-in-only (where it
		// returns whether the booking was confirmed in time), now also used by self-serve
		// payment flows (gift voucher, anytime ticket, parking) to capture side effects
		// like marketing-acknowledgment before the user is redirected to the payment
		// provider. A throw aborts payment; the returned boolean is metadata for onSuccess.
		if (beforePayment) {
			try {
				confirmAndCheckIn = await beforePayment();
			} catch (error) {
				confirmAndCheckIn = false;
				setPaymentProcessing(false);
				setTabIsLoading(false);
				return;
			}
		}

		if (paymentMethod === 'credit-card-option') {
			// Defer to next tick so any state updates flush before we hit the provider SDK.
			// Access the ref inside setTimeout so we get the re-rendered handle with fresh closures.
			setTimeout(async () => {
				const cardForm = provider === 'STRIPE' ? cardFormRef.current : squareFormRef.current;
				if (!cardForm) {
					handleProviderNotLoaded();
					return;
				}
				try {
					const success = await cardForm.submit();
					if (success) {
						onSuccess(confirmAndCheckIn);
					} else {
						// Submit reported failure — the child form should have surfaced an
						// error via handleError, but reset loading defensively in case the
						// failure path inside the child didn't clear the flags.
						setPaymentProcessing(false);
						setTabIsLoading(false);
					}
				} catch (submitError) {
					console.error('Card payment submit threw:', submitError);
					handleError('An unexpected error occurred during payment.');
				}
			}, 0);
			return;
		}

		if (isSavedCard) {
			setTimeout(async () => {
				const cardForm = provider === 'STRIPE' ? cardFormRef.current : squareFormRef.current;
				if (!cardForm) {
					handleProviderNotLoaded();
					return;
				}
				try {
					const success = await cardForm.submit(paymentMethod);
					if (success) {
						onSuccess(confirmAndCheckIn);
					} else {
						setPaymentProcessing(false);
						setTabIsLoading(false);
					}
				} catch (submitError) {
					console.error('Saved card payment submit threw:', submitError);
					handleError('An unexpected error occurred during payment.');
				}
			}, 0);
			return;
		}

		if (paymentMethod === 'invoice-option') {
			try {
				await invoicePayment(transactionId, eventBooking, inCheckIn);
				onSuccess(confirmAndCheckIn);
			} catch (invoiceError) {
				// Without this catch a rejected invoice-payment request (e.g. a 403 when the
				// customer's type doesn't allow invoicing) left paymentProcessing stuck true,
				// hanging the Complete payment modal on an infinite spinner. Surface the error
				// and clear the loading state instead.
				console.error('Invoice payment failed:', invoiceError);
				handleError('Could not send invoice. Please try a different payment method.');
			}
			return;
		}

		if (paymentMethod === 'pay-later') {
			const transactionIds = await payLater(transactionId, inCheckIn);
			onSuccess(confirmAndCheckIn, transactionIds);
			return;
		}

		if (paymentMethod === 'cash-option') {
			newConfirmModal(
				'Confirm amount',
				<FormattedMessage
					id="booking-wizard__cash-payment__confirmation-modal__message"
					defaultMessage="Please confirm you have taken the correct amount of cash before proceeding."
				/>,
				{
					cancelText: 'Back',
					confirmText: 'Confirm',
					shouldCloseOnEsc: false,
					shouldCloseOnOverlayClick: false,
				},
			)
				.then(async () => {
					// Stop any in-flight terminal payment before recording cash so we don't race
					// its poll or leave the reader live to charge the card afterwards.
					await stopTerminalPolling();
					cashPayment(transactionId, inCheckIn)
						.then(() => {
							onSuccess(confirmAndCheckIn);
						})
						.catch(err => {
							const serverMessage = err?.response?.data;
							alertToast(
								typeof serverMessage === 'string' && serverMessage.length > 0
									? serverMessage
									: 'Could not process cash payment',
								'error',
							);
							setPaymentProcessing(false);
							setTabIsLoading(false);
						});
				})
				.catch(() => {
					setPaymentProcessing(false);
					setTabIsLoading(false);
				});
			return;
		}

		if (paymentMethod === 'eftpos-option') {
			try {
				// Stop any in-flight terminal payment before recording EFTPOS so we don't race
				// its poll or leave the reader live to charge the card afterwards.
				await stopTerminalPolling();
				await eftposPayment(transactionId, inCheckIn);
				onSuccess(confirmAndCheckIn);
			} catch (eftposError: any) {
				const serverMessage = eftposError?.response?.data;
				alertToast(
					typeof serverMessage === 'string' && serverMessage.length > 0
						? serverMessage
						: 'Could not process EFTPOS payment',
					'error',
				);
				setPaymentProcessing(false);
				setTabIsLoading(false);
			}
			return;
		}

		if (paymentMethod === 'terminal-option') {
			try {
				const terminal = await terminalPaymentModal();

				// Set ref before the API call so cancel can detect an active terminal payment
				terminalPollingRef.current = { timeout: undefined as unknown as NodeJS.Timeout, terminal };
				await createTerminalPayment(transactionId, terminal, eventBooking, inCheckIn);

				// If cancelled during createTerminalPayment, stop here
				if (!terminalPollingRef.current) {
					return;
				}

				// Sending to the reader extends the hold server-side; have any countdown on screen pick
				// that up rather than expiring the reservation mid-tap on its stale time (#2721).
				dispatchResetReservationEvent();

				let consecutiveTransientFailures = 0;
				const poll = async () => {
					try {
						const res = await checkTerminalPayment(transactionId);
						consecutiveTransientFailures = 0;
						if (res.data === true) {
							if (terminalPollingRef.current) {
								terminalPollingRef.current = null;
								onSuccess(confirmAndCheckIn);
							}
						} else if (terminalPollingRef.current) {
							terminalPollingRef.current.timeout = setTimeout(poll, 1000);
						}
					} catch (err) {
						// #2721: a single failed poll used to end the payment on screen while the reader carried
						// on and took the card. Only the server's answer is final (a 4xx: failed, cancelled, no
						// longer payable); a dropped connection, a gateway error during a deploy or a 5xx says
						// nothing about the payment, so keep asking for a while before giving up.
						if (
							terminalPollingRef.current
							&& isTransientPollError(err)
							&& consecutiveTransientFailures < MAX_TRANSIENT_POLL_FAILURES
						) {
							consecutiveTransientFailures += 1;
							terminalPollingRef.current.timeout = setTimeout(poll, 2000);
							return;
						}
						if (terminalPollingRef.current) {
							terminalPollingRef.current = null;
							alertToast(
								intl.formatMessage({
									id: 'pos__payment-option__error',
									defaultMessage: 'Could not process POS payment',
								}),
								'error',
							);
							setPaymentProcessing(false);
							setTabIsLoading(false);
						}
					}
				};

				terminalPollingRef.current.timeout = setTimeout(poll, 1000);
			} catch (err: any) {
				terminalPollingRef.current = null;
				const errorData = err?.response?.data;
				const message =
					typeof errorData === 'string' && errorData.length > 0
						? errorData
						: intl.formatMessage({
								id: 'pos__payment-option__error',
								defaultMessage: 'Could not process POS payment',
							});
				alertToast(message, 'error');
				setPaymentProcessing(false);
				setTabIsLoading(false);
			}
		}
	};

	return (
		<form id={PAYMENT_FORM_ID} onSubmit={handleSubmit}>
			{/*
			 * Card fields unavailable. Shown only while card is the selected method — on cash or
			 * invoice a card problem is not the customer's concern, and the method they have chosen
			 * still works because this form is still here.
			 */}
			<If condition={isCardPayment && cardFieldsState?.kind === 'loading'}>
				<LottieSpinner />
			</If>
			<If condition={isCardPayment && cardFieldsState?.kind === 'unavailable'}>
				<PaymentUnavailable
					reason={cardFieldsState?.kind === 'unavailable' ? cardFieldsState.reason : 'config'}
					onRetry={cardFieldsState?.kind === 'unavailable' ? cardFieldsState.onRetry : undefined}
				/>
			</If>
			<If condition={isCardPayment && !cardFieldsState && provider === 'STRIPE'}>
				<StripeCheckoutForm
					ref={cardFormRef}
					tabIsLoading={tabIsLoading}
					paymentProcessing={paymentProcessing}
					disableContinueButton={disableContinueButton}
				/>
			</If>
			<If condition={isCardPayment && !cardFieldsState && provider === 'SQUARE'}>
				<SquareCheckoutForm
					ref={squareFormRef}
					tabIsLoading={tabIsLoading}
					paymentProcessing={paymentProcessing}
					disableContinueButton={disableContinueButton}
					onWalletPaymentBefore={async () => {
						if (beforePayment) {
							return beforePayment();
						}
						return true;
					}}
					onWalletPaymentSuccess={confirmAndCheckIn => {
						if (onConfirm) {
							onConfirm(confirmAndCheckIn ?? true);
						} else {
							// Use onWizardSuccess so booking wizard data and the transaction
							// ID are cleared from storage, same as the regular card path.
							onWizardSuccess(
								navigate,
								transactionId,
								isAlteration,
								tripType === 'return',
								eventBooking,
								userId,
							);
						}
					}}
				/>
			</If>

			<If condition={includeSubmitButtons}>
				<Flex className="mt-xs mb-sm gift-payment__buttons" gap="xs">
					<Button
						display={Display.Outline}
						colors={Colors.Black}
						disabled={tabIsLoading}
						newStyles
						onClick={async () => {
							await clearTerminalPolling();
							navigate(onCancelRedirectUrl ?? '');
						}}
					>
						Cancel
					</Button>
					<Button
						className="width-100"
						display={Display.Solid}
						colors={Colors.Primary}
						newStyles
						type="submit"
						disabled={(disableContinueButton || tabIsLoading) && paymentMethod === 'credit-card-option'}
						buttonProps={{
							form: PAYMENT_FORM_ID,
						}}
					>
						{tabIsLoading ? 'Processing...' : 'Pay now'}
					</Button>
				</Flex>
			</If>
		</form>
	);
}

export default observer(CheckOutForm);
